import { type Address, RecipientType } from '@this+that/models';

import type { DraftRequestContent } from '../../hooks/UserCommunications/draft/draftRequest';
import type { ComposeSnapshot } from '../../models/composeSnapshot';
import { rebuildAttachments } from '../../pages/private/Inbox/components/hooks/composeSnapshotRestore';
import type { DraftData } from '../../pages/private/Inbox/components/hooks/useSmartDraftManager';
import { handleSelectDraftReqType } from '../handleSelectDraftReqType';

export interface TypedRecipient extends Address {
  type: RecipientType;
}

export const toTypedRecipients = (
  data: Pick<DraftData, 'toAddresses' | 'ccAddresses' | 'bccAddresses'>,
): TypedRecipient[] => [
  ...data.toAddresses.map((a) => ({ ...a, type: RecipientType.TO })),
  ...data.ccAddresses.map((a) => ({ ...a, type: RecipientType.CC })),
  ...data.bccAddresses.map((a) => ({ ...a, type: RecipientType.BCC })),
];

export class SnapshotAttachmentsError extends Error {}

// Sending without a file that failed to rebuild would silently drop it.
const rebuildAllAttachments = (snapshot: ComposeSnapshot) => {
  const { attachments, failedNames } = rebuildAttachments(snapshot.attachments);
  if (failedNames.length > 0) {
    throw new SnapshotAttachmentsError(failedNames.join(', '));
  }
  return attachments;
};

export const snapshotToDraftData = (snapshot: ComposeSnapshot): DraftData => ({
  subject: snapshot.subject,
  message: snapshot.message,
  toAddresses: snapshot.toAddresses,
  ccAddresses: snapshot.ccAddresses,
  bccAddresses: snapshot.bccAddresses,
  attachments: rebuildAllAttachments(snapshot),
  fromEmail: snapshot.fromEmail,
  messageId: snapshot.sourceMessageId,
  modifiedQuotedContent: snapshot.modifiedQuotedContent,
});

export const snapshotToDraftRequestContent = (
  snapshot: ComposeSnapshot,
  data: DraftData,
): DraftRequestContent => ({
  content: data.message,
  subject: data.subject,
  recipients: toTypedRecipients(data),
  attachments: data.attachments,
  draftId: snapshot.draftId,
  messageId: snapshot.sourceMessageId,
  draftType: handleSelectDraftReqType(snapshot.handler),
  modifiedQuotedContent: snapshot.modifiedQuotedContent ?? undefined,
  expectedDraftVersion: snapshot.draftVersion,
  attachmentsToRemove: snapshot.attachmentsToRemove,
});
